import { useState, useRef, type CSSProperties } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Plus,
  FolderPlus,
  MoreVertical,
  GripVertical,
  EyeOff,
  Eye,
  Users,
  ArrowRightLeft,
} from 'lucide-react';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { TodoItemRow } from './TodoItemRow';
import { CountBadge } from './CountBadge';
import { GiveAccessDialog } from './GiveAccessDialog';
import { useTodoListContext } from '../context';
import { itemContainerId } from '../lib/dnd';
import { useSectionCollapse } from '../lib/collapsedSections';
import type { TodoSectionResponse, TodoItemResponse } from '@/lib/api';

interface SectionCardProps {
  section: TodoSectionResponse;
  sections: TodoSectionResponse[];
  items: TodoItemResponse[];
  /** Whether an ancestor section is hidden from public. The hide gate is
   * inherited, so a section under a hidden ancestor is itself hidden — this
   * flows the ancestor state down so nested rows show the effective state. */
  ancestorHiddenFromPublic?: boolean;
}

export function SectionCard({
  section,
  sections,
  items,
  ancestorHiddenFromPublic = false,
}: SectionCardProps) {
  const {
    isOwner,
    isListPublic,
    currentPrincipalId,
    visibleSectionIds,
    onEditSection,
    onDeleteSection,
    onMoveSection,
    onCreateItem,
    onCreateSection,
    onToggleHiddenFromPublic,
    onSetSectionAccess,
    sectionAccessBySectionId,
    accessibleSectionIds,
    assignableMembers,
    resolvePrincipalName,
    unreadAssignedSectionIds,
    onViewAssignment,
  } = useTodoListContext();

  const isUnreadAssignment = unreadAssignedSectionIds?.has(section.sectionId) ?? false;
  // Collapse state is persisted per-section (localStorage) so a minimized
  // section stays minimized across navigation and refresh.
  const { expanded, toggle: toggleCollapsed, setCollapsed } = useSectionCollapse(section.sectionId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [quickAddActive, setQuickAddActive] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const quickAddRef = useRef<HTMLInputElement>(null);
  const [quickAddCategoryActive, setQuickAddCategoryActive] = useState(false);
  const [quickAddCategoryName, setQuickAddCategoryName] = useState('');
  const quickAddCategoryRef = useRef<HTMLInputElement>(null);

  const isTimeframe = section.sectionType === 'timeframe';
  // The sub-section quick-add creates a child of type 'category' in both cases
  // (there is no third section type); only the wording differs — a timeframe
  // groups "categories", a category nests "sections".
  const addSubLabel = isTimeframe ? 'Add category' : 'Add section';
  const addSubPlaceholder = isTimeframe ? 'New category name...' : 'New section name...';
  const childSections = sections
    .filter((s) => s.parentSectionId === section.sectionId)
    .filter((s) => !visibleSectionIds || visibleSectionIds.has(s.sectionId))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const sectionItems = items
    .filter((i) => i.sectionId === section.sectionId)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const itemIds = sectionItems.map((i) => i.itemId);
  const childSectionIds = childSections.map((s) => s.sectionId);

  // Header count = open top-level tasks in this section AND every section
  // nested under it, so a collapsed parent still says how much work it holds.
  // Walks the full `sections` list (not the visibility-gated children) so a
  // search that hides an empty child never changes the parent's number.
  const subtreeSectionIds = new Set<string>([section.sectionId]);
  let frontier = [section.sectionId];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const s of sections) {
      if (
        s.parentSectionId &&
        frontier.includes(s.parentSectionId) &&
        !subtreeSectionIds.has(s.sectionId)
      ) {
        subtreeSectionIds.add(s.sectionId);
        next.push(s.sectionId);
      }
    }
    frontier = next;
  }
  const subtreeCount = items.filter(
    (i) => i.sectionId && subtreeSectionIds.has(i.sectionId),
  ).length;

  // Two distinct rights (server-enforced; this drives affordances only):
  //  - Managing the SECTION itself (rename / delete / hide / grant access) is
  //    owner/admin only.
  //  - Managing the TASKS inside a section is allowed for a manager OR a member
  //    with access to this section (directly or via an ancestor grant — the
  //    subsection cascade, precomputed into accessibleSectionIds).
  const principalsWithAccess = sectionAccessBySectionId?.get(section.sectionId) ?? [];
  const hasSectionAccess = accessibleSectionIds?.has(section.sectionId) ?? false;
  // Owners/admins can already work in every section by virtue of their role, so
  // they're never offered as grantees in the "Give access" dialog.
  const grantableMembers = (assignableMembers ?? []).filter(
    (m) => m.role !== 'owner' && m.role !== 'admin',
  );
  const canManageSection = isOwner;
  const canManageSectionTasks = isOwner || hasSectionAccess;
  // Public-visibility gate. `hiddenByAncestor` means a parent section is hidden,
  // so this whole subtree is hidden regardless of this section's own flag;
  // `effectiveHidden` is what the public view actually applies. The inline eye
  // is owner-only chrome shown only on a public list.
  const hiddenByAncestor = ancestorHiddenFromPublic;
  const effectiveHidden = section.hiddenFromPublic || hiddenByAncestor;
  const showVisibilityEye = !!isListPublic && isOwner;
  const canEditItemInSection = (item: TodoItemResponse): boolean =>
    isOwner ||
    hasSectionAccess ||
    (!!currentPrincipalId && item.assignedToPrincipalId === currentPrincipalId);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionId,
    data: { type: 'section' },
    disabled: !isOwner,
  });
  // Drop-zone so items (dragged in the shared ListDetailPage context) can land in
  // this section; distinct id from the section sortable to avoid collisions.
  const { setNodeRef: setItemDropRef } = useDroppable({ id: itemContainerId(section.sectionId) });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        isTimeframe
          ? 'border-l-4 border-primary/40 rounded-r-lg bg-muted/30 p-3 mb-4'
          : 'border border-border rounded-lg bg-card p-3 mb-3'
      }
    >
      <div className="flex items-center gap-2 group">
        {isOwner && (
          <button
            type="button"
            className="cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            // Expanding/clicking the section header counts as viewing its
            // assignment — clears just this section's unread dot.
            if (isUnreadAssignment) onViewAssignment?.('section', section.sectionId);
            toggleCollapsed();
          }}
          className="flex items-center gap-1.5 min-w-0 py-1 hover:text-primary transition-colors"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} section "${section.name}"`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          {isUnreadAssignment && (
            <span
              className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0"
              title="New assignment — click to dismiss"
              aria-label="New assignment"
            />
          )}
          <span
            className={`truncate text-left ${
              isTimeframe ? 'text-base font-semibold' : 'text-sm font-medium text-muted-foreground'
            } ${isListPublic && effectiveHidden ? 'opacity-60' : ''}`}
          >
            {section.name}
          </span>
          <CountBadge count={subtreeCount} />
          {/* Passive "hidden" glyph — shown to members who can't toggle it (the
              owner gets the interactive eye on the right instead, so we don't
              double up). */}
          {isListPublic && effectiveHidden && !showVisibilityEye && (
            <span title="Hidden from public view">
              <EyeOff className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
            </span>
          )}
        </button>

        {principalsWithAccess.length > 0 && resolvePrincipalName && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 gap-1 text-muted-foreground flex-shrink-0"
            title={`Access: ${principalsWithAccess.map((p) => resolvePrincipalName(p)).join(', ')}`}
          >
            <Users className="h-2.5 w-2.5" />
            {principalsWithAccess.length === 1
              ? resolvePrincipalName(principalsWithAccess[0])
              : `${principalsWithAccess.length} people`}
          </Badge>
        )}

        {canManageSectionTasks && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={() => {
              setQuickAddActive(true);
              setCollapsed(false);
              setTimeout(() => quickAddRef.current?.focus(), 0);
            }}
            aria-label={`Add item to "${section.name}"`}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}

        {isOwner && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={() => {
              setQuickAddCategoryActive(true);
              setCollapsed(false);
              setTimeout(() => quickAddCategoryRef.current?.focus(), 0);
            }}
            aria-label={`${addSubLabel} to "${section.name}"`}
            title={addSubLabel}
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </Button>
        )}

        <div className="flex-1" />

        {/* Section public-visibility eye — owner-only, public lists only. The
            gate is inherited: hiding a section hides its tasks and sub-sections.
            When a parent is already hidden this is a disabled indicator (toggling
            this section can't make its subtree public while an ancestor hides it). */}
        {showVisibilityEye &&
          (hiddenByAncestor ? (
            <span
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-muted-foreground/40"
              title="Hidden from public — a parent section is hidden"
              aria-label="Hidden from public because a parent section is hidden"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </span>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 flex-shrink-0 ${
                section.hiddenFromPublic ? 'text-muted-foreground' : 'text-muted-foreground/60'
              }`}
              onClick={() =>
                onToggleHiddenFromPublic?.(section.sectionId, !section.hiddenFromPublic)
              }
              aria-label={
                section.hiddenFromPublic
                  ? `Show section "${section.name}" in the public view`
                  : `Hide section "${section.name}" from the public view`
              }
              title={
                section.hiddenFromPublic
                  ? 'Section hidden from public — click to show (also affects its tasks & sub-sections)'
                  : 'Section visible publicly — click to hide (also affects its tasks & sub-sections)'
              }
            >
              {section.hiddenFromPublic ? (
                <EyeOff className="h-3.5 w-3.5" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </Button>
          ))}

        {canManageSection && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                aria-label={`Section actions for "${section.name}"`}
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditSection?.(section)}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Edit
              </DropdownMenuItem>
              {onSetSectionAccess && grantableMembers.length > 0 && (
                <DropdownMenuItem onClick={() => setAccessDialogOpen(true)}>
                  <Users className="h-3.5 w-3.5 mr-2" />
                  Give access
                </DropdownMenuItem>
              )}
              {/* Public visibility is now the inline eye on the header (owner +
                  public list) — no redundant menu item. */}
              {/* Move the whole section subtree (sub-sections + tasks) to
                  another list in the workspace — manager-only. */}
              {isOwner && onMoveSection && (
                <DropdownMenuItem onClick={() => onMoveSection(section)}>
                  <ArrowRightLeft className="h-3.5 w-3.5 mr-2" />
                  Move to list…
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {expanded ? (
        <div className="mt-1">
          {quickAddActive && (
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="h-4 w-4 flex-shrink-0" />
              <Input
                ref={quickAddRef}
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                placeholder="New item title..."
                className="h-8 text-sm"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && quickAddTitle.trim()) {
                    const title = quickAddTitle.trim();
                    setQuickAddActive(false);
                    setQuickAddTitle('');
                    await onCreateItem?.({ title, sectionId: section.sectionId });
                  }
                  if (e.key === 'Escape') {
                    setQuickAddActive(false);
                    setQuickAddTitle('');
                  }
                }}
                onBlur={async () => {
                  const title = quickAddTitle.trim();
                  setQuickAddActive(false);
                  setQuickAddTitle('');
                  if (title) {
                    await onCreateItem?.({ title, sectionId: section.sectionId });
                  }
                }}
              />
            </div>
          )}

          <div ref={setItemDropRef} className="min-h-[8px]">
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              {sectionItems.map((item) => (
                <TodoItemRow
                  key={item.itemId}
                  item={item}
                  canEdit={canEditItemInSection(item)}
                  canManageSection={canManageSectionTasks}
                  sectionHidden={effectiveHidden}
                />
              ))}
            </SortableContext>
          </div>

          {quickAddCategoryActive && (
            <div className="flex items-center gap-3 px-3 py-2">
              <FolderPlus className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <Input
                ref={quickAddCategoryRef}
                value={quickAddCategoryName}
                onChange={(e) => setQuickAddCategoryName(e.target.value)}
                placeholder={addSubPlaceholder}
                className="h-8 text-sm"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && quickAddCategoryName.trim()) {
                    const name = quickAddCategoryName.trim();
                    setQuickAddCategoryActive(false);
                    setQuickAddCategoryName('');
                    await onCreateSection?.({
                      name,
                      parentSectionId: section.sectionId,
                      sectionType: 'category',
                    });
                  }
                  if (e.key === 'Escape') {
                    setQuickAddCategoryActive(false);
                    setQuickAddCategoryName('');
                  }
                }}
                onBlur={async () => {
                  const name = quickAddCategoryName.trim();
                  setQuickAddCategoryActive(false);
                  setQuickAddCategoryName('');
                  if (name) {
                    await onCreateSection?.({
                      name,
                      parentSectionId: section.sectionId,
                      sectionType: 'category',
                    });
                  }
                }}
              />
            </div>
          )}

          <SortableContext items={childSectionIds} strategy={verticalListSortingStrategy}>
            <div className="ml-4">
              {childSections.map((child) => (
                <SectionCard
                  key={child.sectionId}
                  section={child}
                  sections={sections}
                  items={items}
                  ancestorHiddenFromPublic={effectiveHidden}
                />
              ))}
            </div>
          </SortableContext>

          {sectionItems.length === 0 &&
            childSections.length === 0 &&
            !quickAddActive &&
            !quickAddCategoryActive && (
              <p className="text-xs text-muted-foreground px-3 py-2">No items in this section</p>
            )}
        </div>
      ) : (
        // Collapsed: keep the item drop-zone mounted so a task can still be
        // dragged into this section (dnd-kit only registers droppables that are
        // in the DOM). The expanded branch attaches the same ref to its list.
        <div ref={setItemDropRef} className="min-h-[12px]" aria-hidden="true" />
      )}

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {isTimeframe ? 'Timeframe' : 'Section'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{section.name}&rdquo;? All items in this{' '}
              {isTimeframe ? 'timeframe' : 'section'} will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDeleteSection?.(section.sectionId);
                setConfirmDeleteOpen(false);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {onSetSectionAccess && grantableMembers.length > 0 && (
        <GiveAccessDialog
          open={accessDialogOpen}
          onClose={() => setAccessDialogOpen(false)}
          sectionName={section.name}
          members={grantableMembers}
          currentPrincipalIds={principalsWithAccess}
          onSave={(principalIds) => onSetSectionAccess(section.sectionId, principalIds)}
        />
      )}
    </div>
  );
}
